import {useQuery} from "@tanstack/react-query";
import {API} from "api";
import type {HiddenSelectorOptions} from "libs/selectorVisibility";

/**
 * Per-channel editorial settings (GET /api/v3/channel-settings).
 *
 * `stageOrder` is the admin-saved, channel-wide display order of the Stage
 * (content-type) options; null means "nobody saved one — keep the declared
 * products.js order". The `hidden*` lists are the products, sets and
 * backgrounds the selectors do not offer (libs/selectorVisibility.ts). Every
 * user's dropdowns read this, so the query is public and cheap; the writes
 * live in the admin DefaultScenePanel.
 */

export const getChannelSettingsQueryKey = (channel: string | undefined) =>
  ["channel-settings", channel ?? null] as const;

/**
 * The hidden lists are optional: a server from before them sends only
 * `stageOrder`. Read them through the helpers of libs/selectorVisibility.ts.
 */
export interface ChannelSettings extends Partial<HiddenSelectorOptions> {
  channel: string;
  stageOrder: string[] | null;
}

export function useChannelSettings(channel: string | undefined) {
  return useQuery<ChannelSettings>({
    queryKey: getChannelSettingsQueryKey(channel),
    queryFn: () => API.fetchChannelSettings(channel),
    enabled: Boolean(channel),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    // A backend that predates the endpoint 404s; the declared order applies
    // and retrying cannot change that.
    retry: false,
  });
}
